import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import logo from '../images/rustle_it_logo.png';

const ChangePassword: React.FC = () => {
    const navigate = useNavigate();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'error' | 'success', text: React.ReactNode } | null>(null);

    const handleSubmit = async () => {
        setMessage(null);

        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match.' });
            return;
        }

        if (!oldPassword || !newPassword) {
            setMessage({ type: 'error', text: 'Please fill in all fields.' });
            return;
        }

        setIsLoading(true);

        try {
            const user = await Auth.currentAuthenticatedUser();
            await Auth.changePassword(user, oldPassword, newPassword);
            setMessage({ type: 'success', text: 'Password changed successfully.' });
            
            // Clear fields or redirect after delay
            setTimeout(() => {
                navigate('/');
            }, 1500);

        } catch (err: any) {
            console.error('Change password error:', err);
            
            let errorContent: React.ReactNode = err.message || 'Failed to change password.';

            // Check for specific Cognito password policy error
            if (err.code === 'InvalidPasswordException' || (err.message && err.message.toLowerCase().includes('password'))) {
                errorContent = (
                    <div>
                        <div>{err.message}</div>
                        <div className="mt-2 fw-bold">Password requirements:</div>
                        <ul className="mb-0 mt-1 ps-3" style={{ textAlign: 'left', fontSize: '0.9em' }}>
                            <li>Minimum 8 characters long</li>
                            <li>At least 1 number</li>
                            <li>At least 1 special character</li>
                            <li>At least 1 uppercase letter</li>
                            <li>At least 1 lowercase letter</li>
                            <li>Previous passwords cannot be reused</li>
                        </ul>
                    </div>
                );
            }

            setMessage({ type: 'error', text: errorContent });
            setIsLoading(false);
        }
    };

    return (
        <div className="login-page rustle-background">
            <div className="login-wrapper">
                <img src={logo} alt="Logo" className="logo" />
                <div className="login-box">
                    <h2 className="font-primary">Change Password</h2>
                    
                    {message && (
                        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
                            {message.text}
                        </div>
                    )}

                    <div className="mb-3">
                        <input 
                            className="form-control" 
                            type="password" 
                            placeholder="Current Password" 
                            value={oldPassword} 
                            onChange={(e) => setOldPassword(e.target.value)} 
                            disabled={isLoading}
                        />
                    </div>
                    
                    <div className="mb-3">
                        <input 
                            className="form-control" 
                            type="password" 
                            placeholder="New Password" 
                            value={newPassword} 
                            onChange={(e) => setNewPassword(e.target.value)} 
                            disabled={isLoading}
                        />
                    </div>
                    
                    <div className="mb-3">
                        <input 
                            className="form-control" 
                            type="password" 
                            placeholder="Confirm New Password" 
                            value={confirmPassword} 
                            onChange={(e) => setConfirmPassword(e.target.value)} 
                            disabled={isLoading}
                        />
                    </div>

                    <div className="d-flex gap-2">
                        <button 
                            className="btn btn-primary flex-grow-1" 
                            onClick={handleSubmit}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Updating...' : 'Update Password'}
                        </button>
                        <button 
                            className="btn btn-secondary" 
                            onClick={() => navigate('/')}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
